<template>
  <div class="d-flex flex-column row-gap-4">
    <div class="d-flex column-gap-4">
      <SearchTextField
        :disabled="noData"
        v-model="computedSearchQuery"
        :tooltipText="$t('project.signed-documents.SignedDocumentsHeader.searchPlaceholder')"
      />
      <app-button
        data-locator="filters-button"
        :disabled="noData"
        type="outlined"
        variant="neutral"
        is-icon-color-custom
        @click="isFiltersPaneOpen = true"
      >
        <template #left-icon>
          <app-material-icon name="instant_mix" />
        </template>
        <span>{{ $t('project.signed-documents.SignedDocumentsHeader.filtersButton') }}</span>
      </app-button>
      <template v-if="display === DISPLAY.grid && filteredSignedDocuments.length > 0">
        <v-checkbox :model-value="areAllSelectedInCurrentPage"
                    :label="$t('project.signed-documents.SignedDocumentsHeader.selectAll')"
                    :indeterminate="areSomeSelectedInCurrentPage"
                    hide-details
                    data-locator="select-all-checkbox"
                    @update:model-value="$emit('toggle-all-selection')"
        />
      </template>
      <app-segmented-control
        v-if="!noResults"
        class="ml-auto"
        :active-option="display"
        :disabled="disabled"
        :options="displayOptions"
        data-locator="display-mode-segmented-control"
        @change-active-option="$emit('update:display', $event)"
      />
    </div>
    <template v-if="selectedSignedDocuments.length > 0">
      <div class="align-center mt-2 mb-1">
        <SignedDocumentsActionBar
          :selection="selectedSignedDocuments"
          @deselect-all="unselectAllSignedDocuments"
          @selectedSignature="selectedSignatures = $event"
        />
      </div>
    </template>
    <template v-if="hasFilters">
      <SignedDocumentsHeaderFilters />
    </template>
    <SignedDocumentsFiltersPane :is-open="isFiltersPaneOpen" @close="isFiltersPaneOpen = false" />
  </div>
</template>

<script>
import { debounce } from 'lodash-es'
import { mapState, mapActions, mapGetters } from 'vuex'

import SearchTextField from '@/common/SearchTextField.vue'
import { DISPLAY } from '@/projects/display'

import SignedDocumentsFiltersPane from './pane/SignedDocumentsFiltersPane.vue'
import SignedDocumentsActionBar from './SignedDocumentsActionBar.vue'
import SignedDocumentsHeaderFilters from './SignedDocumentsHeaderFilters.vue'

export default {
  name: 'SignedDocumentsHeader',
  inheritAttrs: false,
  components: {
    SearchTextField,
    SignedDocumentsFiltersPane,
    SignedDocumentsHeaderFilters,
    SignedDocumentsActionBar,
  },
  inject: [
    'selectedSignedDocuments',
    'unselectAllSignedDocuments',
  ],
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    display: {
      type: String,
      required: true,
    },
    noData: {
      type: Boolean,
      required: true,
    },
    noResults: {
      type: Boolean,
      required: true,
    },
    areAllSelectedInCurrentPage: {
      type: Boolean,
      required: true,
    },
    areSomeSelectedInCurrentPage: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      DISPLAY,
      selectAll: false,
      isFiltersPaneOpen: false,
    }
  },
  computed: {
    ...mapState('signedDocuments', ['signedDocuments', 'searchQuery', 'filters']),
    ...mapGetters('signedDocuments', ['filteredSignedDocuments']),
    selectedSignatures: {
      get () {
        return this.selectedSignedDocuments
      },
      set (val) {
        // TODO : utilisez inject
        this.$emit('selectedSignature', val)
      },
    },
    computedSearchQuery: {
      get () { return this.searchQuery },
      set: debounce(function (val) {
        this.setSearchQuery(val)
      }, 1000),
    },
    hasFilters () {
      return this.filters.startDate || this.filters.endDate || this.filters.signatories.length > 0
    },
    displayOptions () {
      return [
        {
          icon: 'grid_view',
          locatorValue: 'grid-display-option',
          tooltip: this.$t('project.signed-documents.SignedDocumentsHeader.gridDisplay'),
          value: DISPLAY.grid,
        },
        {
          icon: 'list',
          locatorValue: 'table-display-option',
          tooltip: this.$t('project.signed-documents.SignedDocumentsHeader.tableDisplay'),
          value: DISPLAY.table,
        },
      ]
    },
  },
  methods: {
    ...mapActions('signedDocuments', ['setSearchQuery']),
  },
}
</script>
